'use client';

import { allPrimaryItemsCollapsedAtom } from '@/atoms/sideNavAtoms';
import { useIsomorphicLayoutEffect } from '@heidi/utils';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { type FooterMoreRowDescriptor } from '../footer-rail/footer-rail-context';
import { useCommitAfterCollapseExit } from '../footer-rail/use-commit-after-collapse-exit';
import { PrimaryNavRailContext } from './primary-nav-rail-context';
import {
  PRIMARY_NAV_COLLAPSE_EXIT_MS,
  type PrimaryNavRailId,
} from './primary-nav-rail.constants';
import { splitPrimaryNavRail } from './split-primary-nav-rail';
import { usePrimaryNavMeasure } from './use-primary-nav-measure';

interface PrimaryNavRailProviderProps {
  /** Fixed primary-nav descriptors (Home, Scribe, Library, Tasks). */
  descriptors: readonly FooterMoreRowDescriptor[];
  children: ReactNode;
}

/**
 * Owns the collapse decision for the primary nav fixed items.
 * Attaches measurement to the More trigger element (always mounted) so
 * `closest('[data-testid="sidebar-container"]')` finds the sidebar container.
 */
export function PrimaryNavRailProvider({
  descriptors,
  children,
}: PrimaryNavRailProviderProps) {
  const { measurement, containerRef, measure } = usePrimaryNavMeasure();
  const wasOverflowingRef = useRef(false);

  useIsomorphicLayoutEffect(() => {
    measure();
  }, [measure, descriptors]);

  const { collapsedIds: pendingCollapsedIds, moreDescriptors } = useMemo(
    () => splitPrimaryNavRail(descriptors, measurement, wasOverflowingRef.current),
    [descriptors, measurement]
  );

  useEffect(() => {
    wasOverflowingRef.current = moreDescriptors.length > 0;
  }, [moreDescriptors]);

  // Two-phase collapse: items enter collapsingIds for the animation window,
  // then move to collapsedIds (the committed set that triggers unmount).
  // Expansion bypasses the animation and commits immediately.
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<PrimaryNavRailId>>(
    () => pendingCollapsedIds
  );
  const [collapsingIds, setCollapsingIds] = useState<ReadonlySet<PrimaryNavRailId>>(
    () => new Set()
  );
  const committedRef = useRef<ReadonlySet<PrimaryNavRailId>>(pendingCollapsedIds);

  useEffect(() => {
    const committed = committedRef.current;
    const newlyCollapsing = [...pendingCollapsedIds].filter((id) => !committed.has(id));
    const nowExpanding = [...committed].filter((id) => !pendingCollapsedIds.has(id));

    if (newlyCollapsing.length === 0 && nowExpanding.length === 0) return;

    if (nowExpanding.length > 0) {
      // Expansion is instant: commit and clear any in-flight animation ids.
      committedRef.current = pendingCollapsedIds;
      setCollapsedIds(pendingCollapsedIds);
      setCollapsingIds((prev) => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        nowExpanding.forEach((id) => next.delete(id));
        return next;
      });
    }

    if (newlyCollapsing.length === 0) return;

    // Collapse: animate for PRIMARY_NAV_COLLAPSE_EXIT_MS, then commit.
    setCollapsingIds(new Set(newlyCollapsing));
    const timer = setTimeout(() => {
      committedRef.current = pendingCollapsedIds;
      setCollapsedIds(pendingCollapsedIds);
      setCollapsingIds(new Set());
    }, PRIMARY_NAV_COLLAPSE_EXIT_MS);

    return () => clearTimeout(timer);
    // pendingCollapsedIds is a new object whenever splitPrimaryNavRail recomputes,
    // but its reference is stable between measurements (useMemo guards it), so
    // this dep is correct for detecting real changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCollapsedIds]);

  const setAllPrimaryCollapsed = useSetAtom(allPrimaryItemsCollapsedAtom);
  // Use pendingCollapsedIds (not the committed set) so the footer cascade timer
  // starts at the same moment the visual animation begins.
  const areAllItemsCollapsed =
    descriptors.length > 0 && pendingCollapsedIds.size >= descriptors.length;
  useCommitAfterCollapseExit(
    areAllItemsCollapsed,
    setAllPrimaryCollapsed,
    PRIMARY_NAV_COLLAPSE_EXIT_MS
  );

  const isMoreActive = moreDescriptors.some((d) => d.isActive);

  const state = useMemo(
    () => ({ collapsedIds, collapsingIds, moreDescriptors, isMoreActive, containerRef }),
    [collapsedIds, collapsingIds, moreDescriptors, isMoreActive, containerRef]
  );

  return (
    <PrimaryNavRailContext.Provider value={state}>
      {children}
    </PrimaryNavRailContext.Provider>
  );
}
